import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Save, RotateCcw, Clock, Coins, Layers, PlusCircle, X, Copy, DollarSign, Calendar as CalIcon, Trash2, Eye, EyeOff } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";

const BILLING_UNITS = [
  { value: "hour",       label: "Hour",         hint: "Standard hourly rentals" },
  { value: "over",       label: "Over",         hint: "Per-over pricing (bowling machines)" },
  { value: "ball",       label: "Ball × Overs", hint: "Ball-type matrix (tennis / cricket / hard ball)" },
  { value: "fixed_slot", label: "Fixed Slot",   hint: "Pre-set time blocks (e.g. Prime 6-8 PM)" },
  { value: "session",    label: "Session",      hint: "Per coaching session" },
  { value: "day",        label: "Day",          hint: "Per-day / per-night stays" },
  { value: "membership", label: "Membership",   hint: "Monthly/quarterly memberships" },
  { value: "enrollment", label: "Enrollment",   hint: "Structured academy fees" },
];

// Fields visible for each billing unit — drives the show/hide UI so admins
// only see the inputs relevant to how they want to charge.
const FIELD_MAP = {
  hour:       { price: true,  unitsPerHour: true,  hours: true,  slotMins: true,  ballMatrix: false, fixedSlots: false, autoBookingMode: "hourly" },
  over:       { price: true,  unitsPerHour: true,  hours: true,  slotMins: true,  ballMatrix: true,  fixedSlots: false, autoBookingMode: "overs" },
  ball:       { price: false, unitsPerHour: true,  hours: true,  slotMins: true,  ballMatrix: true,  fixedSlots: false, autoBookingMode: "overs" },
  fixed_slot: { price: false, unitsPerHour: false, hours: true,  slotMins: false, ballMatrix: false, fixedSlots: true,  autoBookingMode: "fixed_slots" },
  session:    { price: true,  unitsPerHour: false, hours: false, slotMins: false, ballMatrix: false, fixedSlots: false, autoBookingMode: "hourly" },
  day:        { price: true,  unitsPerHour: false, hours: false, slotMins: false, ballMatrix: false, fixedSlots: false, autoBookingMode: "hourly" },
  membership: { price: true,  unitsPerHour: false, hours: false, slotMins: false, ballMatrix: false, fixedSlots: false, autoBookingMode: "hourly" },
  enrollment: { price: true,  unitsPerHour: false, hours: false, slotMins: false, ballMatrix: false, fixedSlots: false, autoBookingMode: "hourly" },
};

function derivedHourly(unit_price, units_per_hour) {
  const p = parseFloat(unit_price || 0);
  const n = parseFloat(units_per_hour || 0);
  if (!p || !n) return null;
  return p * n;
}

/**
 * Resource List Editor — replaces the old comma-separated field.
 * Each resource is a row (numbered, editable, deletable). Includes:
 *  - "Add resource" button
 *  - "Quick add batch" (adds N items named "<Prefix> <n>")
 *  - Reorder / duplicate row helpers
 */
function ResourceListEditor({ resources, onChange, slug, defaultLabel = "Item" }) {
  const list = resources || [];

  const update = (idx, value) => {
    const next = [...list];
    next[idx] = value;
    onChange(next);
  };
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = () => {
    // Auto-suggest next label based on the first existing prefix
    let prefix = defaultLabel;
    if (list[0]) {
      const m = list[0].match(/^(.*?)[\s\-_]?\d+\s*$/);
      if (m) prefix = m[1].trim();
      else prefix = list[0].replace(/\s?\d+$/, "");
    }
    const nextNum = list.length + 1;
    onChange([...list, `${prefix} ${nextNum}`]);
  };
  const duplicate = (idx) => {
    const src = list[idx];
    onChange([...list.slice(0, idx + 1), `${src} (copy)`, ...list.slice(idx + 1)]);
  };
  const bulkAdd = (count, prefix) => {
    const startNum = list.length + 1;
    const additions = Array.from({ length: count }, (_, i) => `${prefix} ${startNum + i}`);
    onChange([...list, ...additions]);
  };

  const [batchCount, setBatchCount] = useState(4);
  const [batchPrefix, setBatchPrefix] = useState(defaultLabel);

  return (
    <div data-testid={`svc-resources-${slug}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
          Individual resources · {list.length}
        </div>
        <button
          type="button" onClick={add}
          data-testid={`svc-add-resource-${slug}`}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest brand-blue hover:underline"
        >
          <PlusCircle size={11} /> Add {defaultLabel.toLowerCase()}
        </button>
      </div>

      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
        {list.map((name, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`resource-row-${slug}-${i}`}>
            <div className="w-6 text-center text-[10px] font-mono text-white/40">{i + 1}</div>
            <input
              value={name}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`${defaultLabel} name`}
              data-testid={`resource-input-${slug}-${i}`}
              className="flex-1 bg-[#0B1120] border border-white/15 py-1.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
            />
            <button
              type="button" onClick={() => duplicate(i)} title="Duplicate"
              className="p-1.5 text-white/40 hover:brand-blue"
            ><Copy size={12} /></button>
            <button
              type="button" onClick={() => remove(i)} title="Remove"
              data-testid={`resource-remove-${slug}-${i}`}
              className="p-1.5 text-white/40 hover:text-red-400"
            ><X size={12} /></button>
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-xs text-white/40 py-3 text-center border border-dashed border-white/15">
            No resources. Add one, or bulk-add below.
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">Bulk add</span>
        <input
          value={batchPrefix}
          onChange={(e) => setBatchPrefix(e.target.value)}
          placeholder="Prefix"
          data-testid={`bulk-prefix-${slug}`}
          className="flex-1 bg-[#0B1120] border border-white/15 py-1.5 px-2 text-xs"
        />
        <input
          type="number" min="1" max="30"
          value={batchCount}
          onChange={(e) => setBatchCount(parseInt(e.target.value) || 1)}
          data-testid={`bulk-count-${slug}`}
          className="w-16 bg-[#0B1120] border border-white/15 py-1.5 px-2 text-xs"
        />
        <button
          type="button"
          onClick={() => bulkAdd(batchCount, batchPrefix || defaultLabel)}
          data-testid={`bulk-add-${slug}`}
          className="text-[10px] font-mono uppercase tracking-widest bg-brand-orange text-white px-3 py-1.5"
        >
          + Add {batchCount}
        </button>
      </div>
    </div>
  );
}

/** Fixed-slots editor: admin-defined sessions like "Morning · 6-8 AM". */
function FixedSlotsEditor({ slug, slots, openHour = 6, closeHour = 22, onChange }) {
  const update = (i, key, val) => onChange(slots.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const add = () => {
    const nextStart = slots.length ? Math.min(closeHour - 1, (slots[slots.length - 1].start_hour + slots[slots.length - 1].duration_hours)) : (openHour || 6);
    onChange([...slots, { start_hour: nextStart, duration_hours: 2, label: "", price: 0 }]);
  };
  const remove = (i) => onChange(slots.filter((_, idx) => idx !== i));
  const missingPriceCount = slots.filter(s => !(parseFloat(s.price) > 0)).length;

  return (
    <div data-testid={`svc-fixed-slots-${slug}`} className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Pre-defined sessions · {slots.length}</div>
        <button type="button" onClick={add} data-testid={`fs-add-${slug}`} className="text-[10px] font-mono uppercase tracking-widest brand-blue hover:underline flex items-center gap-1">
          <PlusCircle size={11} /> Add session
        </button>
      </div>
      {slots.length === 0 && (
        <div className="text-xs text-white/40 py-3 text-center border border-dashed border-white/15">
          No sessions defined. Add at least one so guests can book.
        </div>
      )}
      {missingPriceCount > 0 && (
        <div className="text-[11px] text-brand-orange bg-brand-orange/10 border border-brand-orange/40 px-3 py-2 mb-2" data-testid={`fs-price-warn-${slug}`}>
          ⚠️ {missingPriceCount} session{missingPriceCount > 1 ? "s" : ""} still without a price — bookings will be blocked until a ₹ is set.
        </div>
      )}
      <div className="space-y-1.5">
        {slots.map((s, i) => {
          const priceMissing = !(parseFloat(s.price) > 0);
          return (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`fs-row-${slug}-${i}`}>
            <input
              type="text" value={s.label || ""}
              onChange={(e) => update(i, "label", e.target.value)}
              placeholder={`e.g. Morning · ${s.start_hour}-${s.start_hour + s.duration_hours}`}
              data-testid={`fs-label-${slug}-${i}`}
              className="col-span-4 bg-[#0B1120] border border-white/15 py-1.5 px-3 text-xs"
            />
            <div className="col-span-2 flex items-center gap-1">
              <span className="text-[10px] font-mono text-white/40">Start</span>
              <input type="number" min="0" max="23" value={s.start_hour}
                onChange={(e) => update(i, "start_hour", parseInt(e.target.value) || 0)}
                data-testid={`fs-start-${slug}-${i}`}
                className="w-14 bg-[#0B1120] border border-white/15 py-1.5 px-2 text-xs font-mono" />
              <span className="text-[10px] font-mono text-white/40">h</span>
            </div>
            <div className="col-span-2 flex items-center gap-1">
              <span className="text-[10px] font-mono text-white/40">Dur</span>
              <input type="number" min="1" max="12" value={s.duration_hours}
                onChange={(e) => update(i, "duration_hours", parseInt(e.target.value) || 1)}
                data-testid={`fs-dur-${slug}-${i}`}
                className="w-14 bg-[#0B1120] border border-white/15 py-1.5 px-2 text-xs font-mono" />
              <span className="text-[10px] font-mono text-white/40">h</span>
            </div>
            <div className={`col-span-3 flex items-center gap-1 ${priceMissing ? "bg-red-500/10 -mx-1 px-1 py-0.5" : ""}`}>
              <span className={`text-[10px] font-mono font-bold ${priceMissing ? "text-red-300" : "brand-orange"}`}>₹</span>
              <input type="number" min="0" step="10" value={s.price ?? ""}
                onChange={(e) => update(i, "price", parseFloat(e.target.value) || 0)}
                data-testid={`fs-price-${slug}-${i}`}
                placeholder="Price"
                className={`flex-1 bg-[#0B1120] border py-1.5 px-2 text-xs font-mono font-bold ${
                  priceMissing ? "border-red-500/60 brand-orange" : "border-brand-orange/50 brand-orange"
                }`} />
            </div>
            <button type="button" onClick={() => remove(i)} data-testid={`fs-remove-${slug}-${i}`} className="col-span-1 text-white/40 hover:text-red-400 flex justify-center">
              <X size={12} />
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Inline pricing-rules editor scoped to one service.
 * Rules override the base per-hour rate for specific windows (weekend, date, day-of-week, time band).
 * Highest priority match wins.
 */
const RULE_TYPES = [
  { v: "weekend",     label: "Weekend",       hint: "Sat + Sun · all day" },
  { v: "day_of_week", label: "Day-of-week",   hint: "Pick specific weekdays" },
  { v: "date",        label: "Date range",    hint: "Between two dates" },
  { v: "time_band",   label: "Time band",     hint: "Hours between X and Y (any day)" },
];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function PricingRulesInline({ serviceSlug }) {
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/pricing-rules");
    setRules((data || []).filter((r) => r.service_slug === serviceSlug));
  };
  useEffect(() => { load(); }, [serviceSlug]);

  const startNew = () => {
    setForm({
      service_slug: serviceSlug,
      rule_type: "weekend",
      rule_value: "",
      price_per_hour: "",
      priority: 10,
      description: "",
      days: [],
      date_from: "",
      date_to: "",
      hour_from: 18,
      hour_to: 22,
    });
    setShowForm(true);
  };

  const save = async () => {
    try {
      let rule_value = "";
      if (form.rule_type === "day_of_week") rule_value = form.days.join(",");
      else if (form.rule_type === "date")   rule_value = `${form.date_from}..${form.date_to}`;
      else if (form.rule_type === "time_band") rule_value = `${form.hour_from}-${form.hour_to}`;
      const payload = {
        service_slug: form.service_slug,
        rule_type: form.rule_type,
        rule_value,
        price_per_hour: parseFloat(form.price_per_hour),
        gst_percent: 0,
        priority: parseInt(form.priority) || 10,
        active: true,
        description: form.description,
      };
      await api.post("/admin/pricing-rules", payload);
      toast.success("Rule added");
      setShowForm(false);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/admin/pricing-rules/${id}`);
    toast.success("Rule removed");
    load();
  };

  const readable = (r) => {
    if (r.rule_type === "weekend") return "Sat + Sun · all day";
    if (r.rule_type === "day_of_week") return (r.rule_value || "").toUpperCase().replace(/,/g, " · ");
    if (r.rule_type === "date") return (r.rule_value || "").replace("..", " → ");
    if (r.rule_type === "time_band") {
      const [a, b] = (r.rule_value || "").split("-");
      return `${a || 0}:00 – ${b || 24}:00 daily`;
    }
    return r.rule_value || "";
  };

  return (
    <div data-testid={`pricing-rules-${serviceSlug}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1">
          <DollarSign size={11} /> Dynamic pricing rules · {rules.length}
        </div>
        <button type="button" onClick={startNew} data-testid={`pr-add-${serviceSlug}`} className="text-[10px] font-mono uppercase tracking-widest brand-blue hover:underline flex items-center gap-1">
          <PlusCircle size={11} /> Add rule
        </button>
      </div>

      {rules.length === 0 && (
        <div className="text-xs text-white/40 py-3 text-center border border-dashed border-white/15">
          No rules. Base unit-price applies to every slot.
        </div>
      )}

      {rules.map((r) => (
        <div key={r.id} data-testid={`pr-row-${r.id}`} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-white/8 last:border-0 text-xs">
          <div className="col-span-3 font-mono uppercase tracking-widest brand-orange">{r.rule_type.replace("_", " ")}</div>
          <div className="col-span-4 text-white/70">{readable(r)}</div>
          <div className="col-span-2 font-mono brand-blue text-right">{formatINR(r.price_per_hour)}/hr</div>
          <div className="col-span-2 text-center text-white/40 font-mono">P{r.priority}</div>
          <button type="button" onClick={() => remove(r.id)} data-testid={`pr-del-${r.id}`} className="col-span-1 text-white/40 hover:text-red-400 flex justify-center">
            <Trash2 size={12} />
          </button>
        </div>
      ))}

      {showForm && form && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#131A2B] border border-white/12 p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display text-lg uppercase font-black">Add rule · {serviceSlug}</h4>
              <button onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {RULE_TYPES.map((rt) => (
                <button key={rt.v} type="button" onClick={() => setForm({ ...form, rule_type: rt.v })}
                  className={`text-left p-2 border transition ${form.rule_type === rt.v ? "border-brand-orange bg-brand-orange/10" : "border-white/15"}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{rt.label}</div>
                  <div className="text-[10px] text-white/50">{rt.hint}</div>
                </button>
              ))}
            </div>

            {form.rule_type === "day_of_week" && (
              <div className="flex flex-wrap gap-1 mb-3">
                {DAY_KEYS.map((d) => (
                  <button key={d} type="button" onClick={() => setForm({ ...form, days: form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d] })}
                    className={`px-3 py-1.5 text-[10px] font-mono uppercase ${form.days.includes(d) ? "bg-brand-blue text-white" : "border border-white/20 text-white/70"}`}>
                    {d}
                  </button>
                ))}
              </div>
            )}
            {form.rule_type === "date" && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} className="bg-transparent border border-white/20 py-2 px-3 text-xs" />
                <input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} className="bg-transparent border border-white/20 py-2 px-3 text-xs" />
              </div>
            )}
            {form.rule_type === "time_band" && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input type="number" min="0" max="23" value={form.hour_from} onChange={(e) => setForm({ ...form, hour_from: parseInt(e.target.value) })} placeholder="From (0-23)" className="bg-transparent border border-white/20 py-2 px-3 text-xs" />
                <input type="number" min="1" max="24" value={form.hour_to} onChange={(e) => setForm({ ...form, hour_to: parseInt(e.target.value) })} placeholder="To (1-24)" className="bg-transparent border border-white/20 py-2 px-3 text-xs" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mb-3">
              <input type="number" value={form.price_per_hour} onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })} placeholder="Override price ₹/hr" className="bg-transparent border border-white/20 py-2 px-3 text-sm" />
              <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} placeholder="Priority (higher wins)" className="bg-transparent border border-white/20 py-2 px-3 text-sm" />
            </div>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm mb-3" />
            <button onClick={save} className="w-full bg-brand-blue text-white py-2.5 text-sm font-bold uppercase tracking-widest">Save rule</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminServicePricing() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirtyMap, setDirtyMap] = useState({});
  const [showNew, setShowNew] = useState(false);
  const emptyNew = { slug: "", name: "", type: "hourly", description: "",
                     icon: "Layers", price_per_hour: "", gst_percent: "18",
                     resources: "" };
  const [newSvc, setNewSvc] = useState(emptyNew);
  const [weekendPrices, setWeekendPrices] = useState({});   // {slug: price_per_hour | null}

  const loadWeekendPrices = async (slugs) => {
    // Find next Saturday for a stable preview date
    const d = new Date();
    d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
    const dateIso = d.toISOString().slice(0, 10);
    const results = await Promise.all(
      slugs.map((slug) =>
        api.get(`/admin/pricing-preview?service_slug=${slug}&date=${dateIso}&start_hour=18`)
          .then((r) => [slug, r.data?.applied_rule?.rule_type === "weekend" ? r.data.price_per_hour : null])
          .catch(() => [slug, null])
      )
    );
    setWeekendPrices(Object.fromEntries(results));
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/service-configs");
      setItems(data);
      setDirtyMap({});
      loadWeekendPrices((data || []).map((it) => it.slug));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (slug, key, value) => {
    setItems((arr) => arr.map((it) => it.slug === slug ? { ...it, [key]: value } : it));
    setDirtyMap((m) => ({ ...m, [slug]: true }));
  };

  // Instant-save the visibility toggle without touching the dirty flag —
  // admins expect the "hide from public" switch to take effect immediately.
  const toggleVisibility = async (row) => {
    const next = !(row.active !== false);
    setItems((arr) => arr.map((it) => it.slug === row.slug ? { ...it, active: next } : it));
    try {
      await api.patch(`/admin/service-configs/${row.slug}`, { active: next });
      toast.success(next ? `${row.name} is now VISIBLE on the public site` : `${row.name} is now HIDDEN from the public site`);
    } catch (e) {
      // rollback
      setItems((arr) => arr.map((it) => it.slug === row.slug ? { ...it, active: !next } : it));
      toast.error(apiError(e));
    }
  };

  const save = async (row) => {
    try {
      const body = {
        booking_mode: row.booking_mode || "hourly",
        fixed_slots: (row.fixed_slots || []).map((fs) => ({
          start_hour: parseInt(fs.start_hour) || 0,
          duration_hours: parseInt(fs.duration_hours) || 1,
          label: fs.label || null,
          price: parseFloat(fs.price) || 0,
          id: fs.id || fs.slug || fs.label || null,
        })),
        billing_unit: row.billing_unit,
        unit_label: row.unit_label,
        unit_price: parseFloat(row.unit_price) || 0,
        units_per_hour: parseFloat(row.units_per_hour) || 0,
        gst_percent: 0,
        open_hour: parseInt(row.open_hour),
        close_hour: parseInt(row.close_hour),
        slot_duration_minutes: parseInt(row.slot_duration_minutes) || 60,
        resources: row.resources,
        image_url: row.image_url || null,
        advance_booking_days: parseInt(row.advance_booking_days) || 30,
        active: !!row.active,
      };
      // Include ball_pricing for ANY service that has a matrix (not just the
      // built-in bowling-machine slug), so custom overs-based services can
      // save their matrix too.
      if (Array.isArray(row.ball_pricing) && row.ball_pricing.length > 0) {
        body.ball_pricing = row.ball_pricing.map((b) => ({
          slug: b.slug, name: b.name,
          tiers: (b.tiers || []).map((t) => ({
            overs: parseInt(t.overs) || 0,
            price: parseFloat(t.price) || 0,
          })).filter((t) => t.overs > 0),
        }));
      }
      await api.patch(`/admin/service-configs/${row.slug}`, body);
      toast.success(`${row.name} saved`);
      setDirtyMap((m) => ({ ...m, [row.slug]: false }));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reset = async (row) => {
    if (!window.confirm(`Reset ${row.name} to catalog defaults? All overrides will be removed.`)) return;
    try {
      await api.delete(`/admin/service-configs/${row.slug}`);
      toast.success(`${row.name} reset to defaults`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteCustom = async (row) => {
    if (!window.confirm(`Delete the custom service "${row.name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/services/${row.slug}`);
      toast.success(`${row.name} deleted`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createService = async () => {
    if (!newSvc.slug || !newSvc.name) {
      toast.error("Slug and name are required");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(newSvc.slug)) {
      toast.error("Slug must be lowercase letters, digits or hyphens only (e.g. table-tennis)");
      return;
    }
    try {
      const body = {
        slug: newSvc.slug.trim(),
        name: newSvc.name.trim(),
        type: newSvc.type,
        description: newSvc.description.trim(),
        icon: newSvc.icon.trim() || "Layers",
        price_per_hour: parseFloat(newSvc.price_per_hour) || 0,
        gst_percent: parseFloat(newSvc.gst_percent) || 0,
        resources: newSvc.resources.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/admin/services", body);
      toast.success(`${body.name} added — configure pricing, images and hours below.`);
      setShowNew(false);
      setNewSvc(emptyNew);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-service-pricing">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Bookings · Service Pricing Config</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Service Pricing Config.</h1>
          <p className="text-sm text-white/60 mt-3 max-w-3xl">
            Everything for a service — booking mode, base price, images, opening hours, advance-booking window and dynamic pricing rules — is right here on the same card. Rules override the base rate for specific days, dates or time bands (highest priority wins).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          data-testid="svc-new-btn"
          className="bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-widest text-xs px-4 py-3 inline-flex items-center gap-2"
        >
          <PlusCircle size={14} /> New Service
        </button>
      </div>

      {/* New Service modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-[#131A2B] border-2 border-brand-orange p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="svc-new-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// New Service</div>
                <h4 className="font-display text-2xl font-black uppercase tracking-tighter mt-1">Add a Service</h4>
              </div>
              <button onClick={() => setShowNew(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Slug (URL-safe)</label>
                  <input
                    value={newSvc.slug}
                    onChange={(e) => setNewSvc({ ...newSvc, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                    placeholder="table-tennis"
                    data-testid="svc-new-slug"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Type</label>
                  <select
                    value={newSvc.type}
                    onChange={(e) => setNewSvc({ ...newSvc, type: e.target.value })}
                    data-testid="svc-new-type"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="batch">Batch (per session)</option>
                    <option value="membership">Membership (monthly)</option>
                    <option value="dormitory">Dormitory (per day)</option>
                    <option value="enrollment">Enrollment (structured)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Display name</label>
                <input
                  value={newSvc.name}
                  onChange={(e) => setNewSvc({ ...newSvc, name: e.target.value })}
                  placeholder="Table Tennis"
                  data-testid="svc-new-name"
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Short description</label>
                <textarea
                  value={newSvc.description}
                  onChange={(e) => setNewSvc({ ...newSvc, description: e.target.value })}
                  placeholder="6 professional TT tables with LED lighting."
                  rows={2}
                  data-testid="svc-new-desc"
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Base ₹ / hr</label>
                  <input
                    type="number" min="0" step="10"
                    value={newSvc.price_per_hour}
                    onChange={(e) => setNewSvc({ ...newSvc, price_per_hour: e.target.value })}
                    placeholder="300"
                    data-testid="svc-new-price"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">GST %</label>
                  <input
                    type="number" min="0" max="28" step="1"
                    value={newSvc.gst_percent}
                    onChange={(e) => setNewSvc({ ...newSvc, gst_percent: e.target.value })}
                    data-testid="svc-new-gst"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Icon</label>
                  <input
                    value={newSvc.icon}
                    onChange={(e) => setNewSvc({ ...newSvc, icon: e.target.value })}
                    placeholder="Layers"
                    data-testid="svc-new-icon"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Resources / courts / units (comma separated)</label>
                <input
                  value={newSvc.resources}
                  onChange={(e) => setNewSvc({ ...newSvc, resources: e.target.value })}
                  placeholder="Table 1, Table 2, Table 3"
                  data-testid="svc-new-resources"
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"
                />
                <div className="text-[10px] text-white/40 mt-1">You can also add / rename resources from the card after creating the service.</div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={createService}
                  data-testid="svc-new-save"
                  className="flex-1 bg-brand-orange hover:bg-white text-black py-3 font-bold text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                >
                  <PlusCircle size={14} /> Create Service
                </button>
                <button
                  onClick={() => setShowNew(false)}
                  className="border border-white/20 hover:bg-white/10 px-6 py-3 font-bold text-xs uppercase tracking-widest"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-white/40 py-6">Loading…</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {items.map((row) => (
          <div key={row.slug} className={`border ${row.active === false ? "border-red-500/50 bg-red-950/20" : "border-white/12 bg-[#131A2B]"} p-6 relative`} data-testid={`svc-cfg-${row.slug}`}>
            {row.active === false && (
              <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-1 text-[10px] font-mono uppercase tracking-widest font-bold" data-testid={`svc-hidden-banner-${row.slug}`}>
                Hidden from public site & booking page
              </div>
            )}
            <div className={`flex items-start justify-between mb-5 gap-3 ${row.active === false ? "mt-4" : ""}`}>
              <div className="flex gap-3 min-w-0">
                {/* Tile-image preview strip */}
                <div className="shrink-0 w-16 h-16 border border-white/12 bg-[#0B1120] overflow-hidden">
                  {row.image_url ? (
                    <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20 text-[9px] font-mono uppercase tracking-widest">no img</div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{row.slug}</div>
                  <div className="font-display text-xl font-black uppercase tracking-tight truncate">{row.name}</div>
                  <div className="text-xs text-white/50 mt-1 max-w-md line-clamp-2">{row.description}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => toggleVisibility(row)}
                  data-testid={`svc-visibility-${row.slug}`}
                  title={row.active === false ? "Currently HIDDEN — click to show on public site" : "Currently VISIBLE — click to hide from public site"}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border text-[10px] font-mono uppercase tracking-widest transition ${
                    row.active === false
                      ? "border-red-500 bg-red-500 text-white hover:bg-red-600"
                      : "border-brand-blue bg-brand-blue/10 brand-blue hover:bg-brand-blue hover:text-white"
                  }`}
                >
                  {row.active === false ? <><EyeOff size={12} /> Hidden</> : <><Eye size={12} /> Visible</>}
                </button>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Base ₹/hr</div>
                <div className="font-display text-lg brand-blue" data-testid={`svc-base-price-${row.slug}`}>
                  {derivedHourly(row.unit_price, row.units_per_hour) != null
                    ? formatINR(derivedHourly(row.unit_price, row.units_per_hour))
                    : "—"}
                </div>
                <WeekendPill baseHourly={derivedHourly(row.unit_price, row.units_per_hour)}
                             weekendPrice={weekendPrices[row.slug]} slug={row.slug} />
              </div>
            </div>

            {/* Booking mode — determines how guests book this service */}
            <div className="mb-5 border border-white/10 bg-[#0B1120] p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
                Booking mode · how guests reserve this service
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { v: "hourly",      label: "Per hour",     hint: "Pick any free hour" },
                  { v: "fixed_slots", label: "Fixed slots",  hint: "Only pre-defined sessions" },
                  { v: "overs",       label: "Per overs",    hint: "Cricket bowling machine" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => patch(row.slug, "booking_mode", opt.v)}
                    data-testid={`svc-mode-${row.slug}-${opt.v}`}
                    className={`text-left p-3 border transition ${
                      (row.booking_mode || "hourly") === opt.v
                        ? "border-brand-orange bg-brand-orange/10"
                        : "border-white/15 hover:border-white/30"
                    }`}
                  >
                    <div className="font-mono text-xs uppercase tracking-widest brand-orange">{opt.label}</div>
                    <div className="text-[10px] text-white/50 mt-1">{opt.hint}</div>
                  </button>
                ))}
              </div>

              {(row.booking_mode || "hourly") === "fixed_slots" && (
                <FixedSlotsEditor
                  slug={row.slug}
                  slots={row.fixed_slots || []}
                  openHour={row.open_hour}
                  closeHour={row.close_hour}
                  onChange={(next) => patch(row.slug, "fixed_slots", next)}
                />
              )}
              {(row.booking_mode || "hourly") === "overs" && (
                <div className="text-[11px] text-white/60 mt-1 p-3 bg-brand-orange/5 border border-brand-orange/20">
                  <b className="brand-orange">Per-overs setup:</b> set the <b>Ball × Overs pricing matrix</b> below (it appears near the bottom of this card). Also set <b>Billing unit</b> to <code className="brand-blue">over</code> and <b>Units-per-hour</b> to the machine&apos;s overs-per-hour rate (e.g. <code className="brand-blue">12</code>).
                </div>
              )}
            </div>

            {/* Tile image uploader */}
            <div className="mb-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
                Tile image · shows on public homepage service tile
              </div>
              <ImageUploader
                value={row.image_url || ""}
                testid={`svc-image-${row.slug}`}
                label={row.image_url ? "Replace image" : "Upload service image"}
                onUploaded={(url) => patch(row.slug, "image_url", url)}
                onCleared={() => patch(row.slug, "image_url", null)}
              />
              <input
                value={row.image_url || ""}
                onChange={(e) => patch(row.slug, "image_url", e.target.value || null)}
                placeholder="…or paste an image URL"
                data-testid={`svc-image-url-${row.slug}`}
                className="mt-2 w-full bg-[#0B1120] border border-white/15 py-2 px-3 text-xs font-mono text-white/70"
              />
            </div>

            {/* Advance booking window + dynamic pricing rules */}
            <div className="mb-5 border border-white/10 bg-[#0B1120] p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1">
                    <CalIcon size={11} /> Advance booking window (days)
                  </div>
                  <input
                    type="number" min="0" max="365"
                    value={row.advance_booking_days ?? 30}
                    onChange={(e) => patch(row.slug, "advance_booking_days", parseInt(e.target.value) || 0)}
                    data-testid={`svc-advance-${row.slug}`}
                    className="w-full bg-transparent border border-white/20 py-2.5 px-3 text-sm"
                  />
                  <div className="text-[10px] text-white/40 mt-1">Guests may only book up to today + N days.</div>
                </label>
                <div className="md:col-span-2 text-xs text-white/50 md:pt-6">
                  Increase this for calm off-seasons, decrease it for popular services so slots don&apos;t get grabbed months in advance.
                </div>
              </div>
              <div className="border-t border-white/10 mt-4 pt-4">
                <PricingRulesInline serviceSlug={row.slug} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {/* Billing unit */}
              <label className="col-span-2 md:col-span-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1"><Layers size={11} /> Billing unit <span className="text-white/30 normal-case tracking-normal">· (how the price is counted — Booking Mode auto-syncs)</span></div>
                <select
                  value={row.billing_unit || "hour"}
                  onChange={(e) => {
                    const v = e.target.value;
                    patch(row.slug, "billing_unit", v);
                    // Auto-sync booking_mode so admin doesn't have to remember
                    // to update both dropdowns. Ball / Fixed Slot force their
                    // matching mode; hour → hourly; others → hourly.
                    const nextMode = (FIELD_MAP[v] || {}).autoBookingMode;
                    if (nextMode && nextMode !== row.booking_mode) {
                      patch(row.slug, "booking_mode", nextMode);
                    }
                  }}
                  data-testid={`svc-billing-${row.slug}`}
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                >
                  {BILLING_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>{`${u.label} — ${u.hint}`}</option>
                  ))}
                </select>
              </label>

              {/* Fields visible for this billing unit — FIELD_MAP decides. */}
              {(() => { const F = FIELD_MAP[row.billing_unit || "hour"] || FIELD_MAP.hour; return (<>

              {/* Unit label — always useful (shown to guests as suffix) */}
              <label>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Unit label</div>
                <input
                  value={row.unit_label || ""}
                  onChange={(e) => patch(row.slug, "unit_label", e.target.value)}
                  placeholder="e.g. per over"
                  data-testid={`svc-unitlabel-${row.slug}`}
                  className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                />
              </label>

              {F.price && (
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1"><Coins size={11} /> Unit price ₹</div>
                  <input
                    type="number" step="0.01"
                    value={row.unit_price ?? ""}
                    onChange={(e) => patch(row.slug, "unit_price", e.target.value)}
                    data-testid={`svc-unitprice-${row.slug}`}
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                  />
                </label>
              )}

              {F.unitsPerHour && (
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Units / hour</div>
                  <input
                    type="number" step="0.01"
                    value={row.units_per_hour ?? ""}
                    onChange={(e) => patch(row.slug, "units_per_hour", e.target.value)}
                    data-testid={`svc-uph-${row.slug}`}
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                  />
                </label>
              )}

              {F.hours && (
                <>
                  <label>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1 flex items-center gap-1"><Clock size={11} /> Open hour</div>
                    <input
                      type="number" min="0" max="23"
                      value={row.open_hour ?? 6}
                      onChange={(e) => patch(row.slug, "open_hour", e.target.value)}
                      data-testid={`svc-openhr-${row.slug}`}
                      className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                    />
                  </label>
                  <label>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Close hour</div>
                    <input
                      type="number" min="1" max="24"
                      value={row.close_hour ?? 23}
                      onChange={(e) => patch(row.slug, "close_hour", e.target.value)}
                      data-testid={`svc-closehr-${row.slug}`}
                      className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                    />
                  </label>
                </>
              )}

              {F.slotMins && (
                <label>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Slot mins</div>
                  <input
                    type="number" min="15" step="15"
                    value={row.slot_duration_minutes ?? 60}
                    onChange={(e) => patch(row.slug, "slot_duration_minutes", e.target.value)}
                    className="w-full bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm focus:outline-none focus:border-brand-blue"
                  />
                </label>
              )}

              </>); })()}

              {/* Resources */}
              <div className="col-span-2 md:col-span-3">
                <ResourceListEditor
                  slug={row.slug}
                  resources={row.resources || []}
                  onChange={(next) => patch(row.slug, "resources", next)}
                  defaultLabel={
                    row.slug.includes("bowling") ? "Machine"
                    : row.slug.includes("net") ? "Net"
                    : row.slug.includes("badminton") ? "Court"
                    : row.slug.includes("box") ? "Box"
                    : row.slug.includes("ground") ? "Ground"
                    : row.slug.includes("dormitory") ? "Bed"
                    : "Slot"
                  }
                />
              </div>

              {/* Bowling machine pricing matrix (Ball type × Overs tier) —
                  shown for booking_mode="overs" OR billing_unit "over"/"ball". */}
              {(row.booking_mode === "overs" || row.billing_unit === "over" || row.billing_unit === "ball" || row.slug === "bowling-machine") && (
                <div className="col-span-2 md:col-span-3" data-testid={`svc-ball-pricing-${row.slug}`}>
                  <BallPricingEditor
                    matrix={row.ball_pricing || []}
                    onChange={(next) => patch(row.slug, "ball_pricing", next)}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-5 gap-3">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => reset(row)}
                  data-testid={`svc-reset-${row.slug}`}
                  className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white"
                >
                  <RotateCcw size={12} /> Reset to defaults
                </button>
                {row._source === "custom" && (
                  <button
                    onClick={() => deleteCustom(row)}
                    data-testid={`svc-delete-${row.slug}`}
                    className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={12} /> Delete service
                  </button>
                )}
              </div>
              <button
                onClick={() => save(row)}
                disabled={!dirtyMap[row.slug]}
                data-testid={`svc-save-${row.slug}`}
                className={`flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-4 py-2 border ${
                  dirtyMap[row.slug]
                    ? "border-brand-blue bg-brand-blue text-white hover:glow-blue"
                    : "border-white/20 text-white/40"
                }`}
              >
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BallPricingEditor({ matrix, onChange }) {
  const addBall = () => {
    const slug = window.prompt("Ball slug (lowercase, e.g. tennis, leather, custom)");
    if (!slug) return;
    const name = window.prompt("Display name", slug.charAt(0).toUpperCase() + slug.slice(1) + " Ball");
    onChange([...(matrix || []), { slug: slug.toLowerCase(), name: name || slug, tiers: [] }]);
  };
  const removeBall = (i) => {
    onChange((matrix || []).filter((_, j) => j !== i));
  };
  const patchTier = (bi, ti, field, value) => {
    const next = [...(matrix || [])];
    const tiers = [...(next[bi].tiers || [])];
    tiers[ti] = { ...tiers[ti], [field]: value };
    next[bi] = { ...next[bi], tiers };
    onChange(next);
  };
  const addTier = (bi) => {
    const next = [...(matrix || [])];
    const tiers = [...(next[bi].tiers || []), { overs: 10, price: 0 }];
    next[bi] = { ...next[bi], tiers };
    onChange(next);
  };
  const removeTier = (bi, ti) => {
    const next = [...(matrix || [])];
    const tiers = (next[bi].tiers || []).filter((_, j) => j !== ti);
    next[bi] = { ...next[bi], tiers };
    onChange(next);
  };

  return (
    <div className="border border-brand-orange/30 bg-brand-orange/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
          // Bowling Matrix · Ball type × Overs tier price
        </div>
        <button
          type="button"
          onClick={addBall}
          data-testid="ball-add-btn"
          className="text-[10px] font-mono uppercase tracking-widest border border-brand-orange text-brand-orange px-3 py-1 hover:bg-brand-orange hover:text-black"
        >
          + Ball Type
        </button>
      </div>
      {(matrix || []).length === 0 && (
        <div className="text-xs text-white/40 py-4 text-center">No ball types configured. Add one to start pricing.</div>
      )}
      <div className="space-y-4">
        {(matrix || []).map((b, bi) => (
          <div key={b.slug || bi} className="border border-white/12 bg-black/30 p-3" data-testid={`ball-row-${b.slug}`}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div>
                <div className="font-display text-sm font-black uppercase">{b.name}</div>
                <div className="font-mono text-[10px] text-white/50">slug: {b.slug}</div>
              </div>
              <button
                type="button"
                onClick={() => removeBall(bi)}
                data-testid={`ball-remove-${b.slug}`}
                className="text-[10px] font-mono uppercase tracking-widest text-red-400 hover:text-red-300 border border-red-500/40 px-2 py-1"
              >
                Remove
              </button>
            </div>
            <div className="space-y-1.5">
              {(b.tiers || []).map((t, ti) => (
                <div key={ti} className="flex items-center gap-2" data-testid={`ball-tier-${b.slug}-${ti}`}>
                  <input
                    type="number" min="1" value={t.overs ?? ""}
                    onChange={(e) => patchTier(bi, ti, "overs", e.target.value)}
                    placeholder="overs"
                    className="w-20 bg-black/50 border border-white/15 py-1.5 px-2 text-xs font-mono"
                  />
                  <span className="text-[10px] font-mono text-white/40">overs @</span>
                  <input
                    type="number" min="0" value={t.price ?? ""}
                    onChange={(e) => patchTier(bi, ti, "price", e.target.value)}
                    placeholder="price"
                    className="w-24 bg-black/50 border border-white/15 py-1.5 px-2 text-xs font-mono"
                  />
                  <span className="text-[10px] font-mono text-white/40">₹</span>
                  <button
                    type="button"
                    onClick={() => removeTier(bi, ti)}
                    className="ml-auto text-[10px] font-mono text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addTier(bi)}
                data-testid={`ball-add-tier-${b.slug}`}
                className="text-[10px] font-mono uppercase tracking-widest text-white/60 hover:text-white border border-white/20 px-2 py-1"
              >
                + Add Tier
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-white/40 mt-3">
        Tip: Add 10, 20, 30 overs tiers per ball type. Guests will pick a ball type first, then an overs tier.
      </div>
    </div>
  );
}


function WeekendPill({ baseHourly, weekendPrice, slug }) {
  if (weekendPrice == null || baseHourly == null) {
    return (
      <div className="text-[10px] font-mono text-white/30" data-testid={`svc-weekend-price-${slug}`}>
        Weekend: same as base
      </div>
    );
  }
  const delta = Math.round(weekendPrice - baseHourly);
  const up = delta > 0;
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-mono" data-testid={`svc-weekend-price-${slug}`}>
      <span className="text-white/40">Weekend</span>
      <span className={up ? "brand-orange font-bold" : "brand-blue font-bold"}>
        {formatINR(weekendPrice)}
      </span>
      {delta !== 0 && (
        <span className={up ? "text-brand-orange/70" : "text-brand-blue/70"}>
          ({up ? "↑" : "↓"}{formatINR(Math.abs(delta))})
        </span>
      )}
    </div>
  );
}

